// src/components/BuildingDesigner/ElementsList.jsx
//
// A stand-alone list of already-committed elements in the plan.
// Lives in the left panel BELOW the ElementForm, so it's always
// visible — even when no element type is selected (which is the
// state right after loading a project from disk).
//
// Shows Edit + Delete buttons per room. Highlights the row that's
// currently being edited.

import React from "react";
import styled from "styled-components";

const Wrap = styled.div`
  margin-top: 16px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
`;
const H4 = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
`;
const Row = styled.div`
  font-size: 12px;
  margin-bottom: 4px;
  padding: 4px;
  background: ${(p) =>
    p.$selected ? "#d4f6f0" : p.$editing ? "#fff8dc" : "transparent"};
  border: 1px solid
    ${(p) => (p.$selected ? "#00d9c0" : p.$editing ? "#f0c040" : "transparent")};
  border-radius: 3px;
  cursor: pointer;
`;
const EditBtn = styled.button`
  margin-left: 6px;
  padding: 2px 6px;
  font-size: 11px;
  background: #fff;
  color: #2e8b57;
  border: 1px solid #bfdac9;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #eef7f0;
  }
`;
const CancelBtn = styled.button`
  margin-left: 6px;
  padding: 2px 6px;
  font-size: 11px;
  background: #fff;
  color: #a06000;
  border: 1px solid #f0c040;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #fff3cd;
  }
`;
const DeleteBtn = styled.button`
  margin-left: 4px;
  padding: 2px 6px;
  font-size: 11px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #f2c1bc;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #fdecea;
  }
`;
const BulkBar = styled.div`
  padding: 6px 8px;
  margin-bottom: 6px;
  background: #d4f6f0;
  border: 1px solid #00d9c0;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const BulkDeleteBtn = styled.button`
  padding: 3px 8px;
  font-size: 11px;
  background: #c0392b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #a5311e;
  }
`;
const BulkClearBtn = styled.button`
  padding: 3px 8px;
  font-size: 11px;
  background: #fff;
  color: #333;
  border: 1px solid #bbb;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #f0f0f0;
  }
`;

export default function ElementsList({
  floor,
  editIndex,
  editKind,
  onEditElement,
  onDeleteElement,
  onCancelEdit,
  selection = [],
  onToggleSelection,
  onClearSelection,
  onDeleteSelected,
  showTexts = true,
  onToggleShowTexts,
  onDeleteText,
  onDeleteOrphanTexts,
  canConvertSelection = false,
  onConvertSelection,
  onConvertToStairs,
  onConvertToShaft,
  canMergeBeams = false,
  onMergeBeams,
  carriedInfo = { ok: false },
  onSetCarried,
  canDefinePanel = false,
  onDefinePanel,
  mirrorPanelOpen = false,
  onToggleMirrorPanel,
  mirrorAxis = "vertical",
  setMirrorAxis,
  mirrorLine = 0,
  setMirrorLine,
  onApplyMirror,
  onCopySelection,
  inputStep = 0.1,
  setInputStep,
  clipboard = null,
  pastePanelOpen = false,
  onTogglePastePanel,
  pasteX = 0,
  setPasteX,
  pasteY = 0,
  setPasteY,
  onApplyPaste,
  onClearClipboard,
  groupChangePending = null,
  onSaveGroupChange,
  onCancelGroupChange,
  groupOriginBBox = null,
  onEditGroupMoveDelta,
  onEditGroupMoveAbsolute,
  onEditGroupResizeDelta,
}) {
  // Fast lookup helper: is this element in the multi-selection?
  const isSelected = (kind, index) =>
    selection.some((s) => s.kind === kind && s.index === index);
  const totalOther =
    (floor.stairs?.length || 0) +
    (floor.shafts?.length || 0) +
    (floor.customs?.length || 0) +
    (floor.beams?.length || 0) +
    (floor.columns?.length || 0) +
    (floor.slabs?.length || 0);
  const totalRooms = floor.rooms?.length || 0;
  // Stage 5e: keep the component mounted whenever the clipboard has
  // content, even on a brand-new empty project — otherwise the Paste
  // panel vanishes right when "New project" makes it most useful.
  const clipboardHasItems = clipboard && (clipboard.items?.length || 0) > 0;
  if (
    totalRooms === 0 &&
    totalOther === 0 &&
    !clipboardHasItems &&
    !groupChangePending
  )
    return null;

  return (
    <Wrap>
      <H4>Elements in plan ({totalRooms + totalOther})</H4>

      {/* Stage 3x.5: toggle text-label overlay on the canvas. Only shown
          if the loaded floor actually has any texts (from a DXF import). */}
      {(floor.texts || []).length > 0 && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            marginBottom: 8,
            cursor: "pointer",
            color: "#333",
          }}
        >
          <input
            type="checkbox"
            checked={!!showTexts}
            onChange={onToggleShowTexts}
          />
          Show text labels ({(floor.texts || []).length})
        </label>
      )}

      {/* Multi-select bulk action bar (Stage 3u ship 1) — only shown
          when the user has at least one element selected. Ctrl+click a
          row or a canvas element to add/remove. */}
      {/* Stage 9a: Copy also available while editing a SINGLE element
          the normal way (click-to-edit, not Ctrl+click) — the page's
          onCopySelection already falls back to editIndex/editKind when
          there's no active multi-selection. */}
      {selection.length === 0 && editIndex !== null && onCopySelection && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 8px",
            marginBottom: 8,
            background: "#eef2ff",
            border: "1px solid #c3cdf0",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <span style={{ color: "#2a3d8f" }}>Editing this element</span>
          <button
            onClick={onCopySelection}
            title="Copy this element to clipboard"
            style={{
              padding: "3px 10px",
              fontSize: 12,
              background: "#fff",
              color: "#2a3d8f",
              border: "1px solid #8ea6e8",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Copy
          </button>
        </div>
      )}
      {selection.length > 0 && (
        <BulkBar>
          <span>
            <strong>{selection.length}</strong> selected
          </span>
          {onCopySelection && (
            <button
              onClick={onCopySelection}
              title="Copy selected elements to clipboard"
              style={{
                padding: "4px 10px",
                fontSize: 12,
                background: "#fff",
                color: "#2a3d8f",
                border: "1px solid #8ea6e8",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Copy
            </button>
          )}
          {setInputStep && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 11, color: "#555" }}>Step:</span>
              <button
                onClick={() => setInputStep(0.1)}
                title="Set drag/resize precision to 0.1 m"
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  background: inputStep === 0.1 ? "#2e8b57" : "#fff",
                  color: inputStep === 0.1 ? "white" : "#333",
                  border: "1px solid #bfdac9",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                0.1 m
              </button>
              <button
                onClick={() => setInputStep(0.01)}
                title="Set drag/resize precision to 0.01 m"
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  background: inputStep === 0.01 ? "#2e8b57" : "#fff",
                  color: inputStep === 0.01 ? "white" : "#333",
                  border: "1px solid #bfdac9",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                0.01 m
              </button>
            </span>
          )}
          {canMergeBeams && onMergeBeams && (
            <button
              onClick={onMergeBeams}
              title="Merge selected collinear beams into one"
              style={{
                padding: "4px 12px",
                fontSize: 12,
                background: "#2e8b57",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Merge Beams
            </button>
          )}
          {carriedInfo && carriedInfo.ok && onSetCarried && (
            <button
              onClick={onSetCarried}
              title="First-clicked beam = carrier, second = carried. Pulls the carried beam's end back to the carrier's face (0.1 m)."
              style={{
                padding: "4px 12px",
                fontSize: 12,
                background: "#00695c",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Set Carried: {carriedInfo.carriedName}
            </button>
          )}
          {canDefinePanel && onDefinePanel && (
            <button
              onClick={onDefinePanel}
              title="Selected beams must form a closed loop. Creates a solid slab (rectangular & <36 m²) or flat slab (otherwise)."
              style={{
                padding: "4px 12px",
                fontSize: 12,
                background: "#1a237e",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Define Panel
            </button>
          )}
          <BulkDeleteBtn
            onClick={onDeleteSelected}
            title="Delete all selected (Delete key)"
          >
            Delete all
          </BulkDeleteBtn>
          <BulkClearBtn
            onClick={onClearSelection}
            title="Clear selection (Esc)"
          >
            Clear
          </BulkClearBtn>
        </BulkBar>
      )}

      {/* Group move/resize pending confirmation bar (Stage 8b) — appears
          after dragging a multi-selection to a new spot, OR dragging one
          selected room's edge to resize the whole group. The change is
          NOT applied yet; the dashed preview stays on canvas until the
          user explicitly confirms, same pattern as single-element
          editing. Δ fields (and, for a move, the absolute x=/y= west/
          south fields) let you fine-tune the MOST RECENT drag — earlier
          resize/move steps already done in this same pending session
          stay applied underneath (Stage 8d gesture stacking). */}
      {groupChangePending && groupChangePending.lastGesture && (
        <div
          style={{
            padding: 8,
            marginBottom: 8,
            background: "#fff8dc",
            border: "1px solid #f0c040",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, color: "#664400", marginBottom: 6 }}>
            {groupChangePending.lastGesture.type === "resize"
              ? "Resize pending"
              : "Move pending"}
          </div>

          {groupChangePending.lastGesture.type === "move" && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 6,
                  flexWrap: "wrap",
                }}
              >
                <span>Δx =</span>
                <input
                  type="number"
                  step="0.1"
                  value={groupChangePending.lastGesture.dx}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    onEditGroupMoveDelta &&
                      onEditGroupMoveDelta(
                        isNaN(v) ? 0 : v,
                        groupChangePending.lastGesture.dy
                      );
                  }}
                  style={{
                    width: 70,
                    padding: "3px 6px",
                    border: "1px solid #e0c060",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <span>Δy =</span>
                <input
                  type="number"
                  step="0.1"
                  value={groupChangePending.lastGesture.dy}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    onEditGroupMoveDelta &&
                      onEditGroupMoveDelta(
                        groupChangePending.lastGesture.dx,
                        isNaN(v) ? 0 : v
                      );
                  }}
                  style={{
                    width: 70,
                    padding: "3px 6px",
                    border: "1px solid #e0c060",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
              </div>
              {groupOriginBBox && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span title="Absolute west edge of the whole selection">
                    x (west) =
                  </span>
                  <input
                    type="number"
                    step="0.1"
                    value={
                      +(
                        groupOriginBBox.minX + groupChangePending.lastGesture.dx
                      ).toFixed(3)
                    }
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (isNaN(v)) return;
                      onEditGroupMoveAbsolute &&
                        onEditGroupMoveAbsolute(
                          v,
                          groupOriginBBox.minY +
                            groupChangePending.lastGesture.dy
                        );
                    }}
                    style={{
                      width: 70,
                      padding: "3px 6px",
                      border: "1px solid #e0c060",
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  />
                  <span title="Absolute south edge of the whole selection">
                    y (south) =
                  </span>
                  <input
                    type="number"
                    step="0.1"
                    value={
                      +(
                        groupOriginBBox.minY + groupChangePending.lastGesture.dy
                      ).toFixed(3)
                    }
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (isNaN(v)) return;
                      onEditGroupMoveAbsolute &&
                        onEditGroupMoveAbsolute(
                          groupOriginBBox.minX +
                            groupChangePending.lastGesture.dx,
                          v
                        );
                    }}
                    style={{
                      width: 70,
                      padding: "3px 6px",
                      border: "1px solid #e0c060",
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  />
                </div>
              )}
            </>
          )}

          {groupChangePending.lastGesture.type === "resize" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <span>
                {groupChangePending.lastGesture.edge === "S" ||
                groupChangePending.lastGesture.edge === "N"
                  ? "height"
                  : "width"}{" "}
                Δ =
              </span>
              <input
                type="number"
                step="0.1"
                value={groupChangePending.lastGesture.delta}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  onEditGroupResizeDelta &&
                    onEditGroupResizeDelta(isNaN(v) ? 0 : v);
                }}
                style={{
                  width: 70,
                  padding: "3px 6px",
                  border: "1px solid #e0c060",
                  borderRadius: 4,
                  fontSize: 12,
                }}
              />
              <span style={{ fontSize: 11, color: "#886600" }}>m</span>
            </div>
          )}

          <div style={{ display: "flex", gap: 6 }}>
            {onSaveGroupChange && (
              <button
                onClick={onSaveGroupChange}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  background: "#2e8b57",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Save changes
              </button>
            )}
            {onCancelGroupChange && (
              <button
                onClick={onCancelGroupChange}
                title="Discard the change (Esc)"
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  background: "#fff",
                  color: "#666",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Clipboard / Paste panel (Stage 5d) — persistent, independent of
          the current selection so it survives New Project / Open Project.
          Shown whenever the clipboard has content. Click-to-place works
          the same way as Mirror's line picker. */}
      {clipboard && clipboard.items.length > 0 && (
        <div
          style={{
            padding: 8,
            marginBottom: 8,
            background: "#f3eefc",
            border: "1px solid #b39ddb",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontWeight: 600, color: "#5e35b1" }}>
              📋 Clipboard: {clipboard.items.length} element
              {clipboard.items.length > 1 ? "s" : ""}
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={onTogglePastePanel}
                style={{
                  padding: "3px 10px",
                  fontSize: 11,
                  background: pastePanelOpen ? "#5e35b1" : "#fff",
                  color: pastePanelOpen ? "white" : "#5e35b1",
                  border: "1px solid #5e35b1",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {pastePanelOpen ? "Close" : "Paste…"}
              </button>
              {onClearClipboard && (
                <button
                  onClick={onClearClipboard}
                  title="Clear clipboard"
                  style={{
                    padding: "3px 8px",
                    fontSize: 11,
                    background: "#fff",
                    color: "#888",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {pastePanelOpen && onApplyPaste && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#5e35b1",
                  marginBottom: 8,
                  padding: 6,
                  background: "#e8def8",
                  borderRadius: 4,
                }}
              >
                Click on the canvas to place the copy — the SW corner of the
                copied elements will land there. Fine-tune with the fields
                below, or keep clicking to move it.
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <span>x =</span>
                <input
                  type="number"
                  value={pasteX}
                  onChange={(e) => setPasteX && setPasteX(e.target.value)}
                  step="0.1"
                  style={{
                    width: 70,
                    padding: "3px 6px",
                    border: "1px solid #b39ddb",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <span>y =</span>
                <input
                  type="number"
                  value={pasteY}
                  onChange={(e) => setPasteY && setPasteY(e.target.value)}
                  step="0.1"
                  style={{
                    width: 70,
                    padding: "3px 6px",
                    border: "1px solid #b39ddb",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
              </div>
              <button
                onClick={onApplyPaste}
                title="Add pasted copies at this position"
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  fontSize: 12,
                  background: "#5e35b1",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Paste copy
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mirror-selection bar (Stage 5a) — appears whenever ≥1 element
          is Ctrl+click-selected. Toggles an inline mini-form for axis +
          mirror-line position; "Apply Mirror" adds NEW mirrored copies
          (originals are untouched). */}
      {selection.length > 0 && onToggleMirrorPanel && (
        <div
          style={{
            padding: 8,
            marginBottom: 8,
            background: "#eef2ff",
            border: "1px solid #8ea6e8",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontWeight: 600, color: "#2a3d8f" }}>
              Mirror {selection.length} element{selection.length > 1 ? "s" : ""}
            </span>
            <button
              onClick={onToggleMirrorPanel}
              style={{
                padding: "3px 10px",
                fontSize: 11,
                background: mirrorPanelOpen ? "#2a3d8f" : "#fff",
                color: mirrorPanelOpen ? "white" : "#2a3d8f",
                border: "1px solid #2a3d8f",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {mirrorPanelOpen ? "Close" : "Mirror…"}
            </button>
          </div>

          {mirrorPanelOpen && onApplyMirror && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                  }}
                >
                  <input
                    type="radio"
                    checked={mirrorAxis === "vertical"}
                    onChange={() => setMirrorAxis && setMirrorAxis("vertical")}
                  />{" "}
                  Vertical (flip left↔right)
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                  }}
                >
                  <input
                    type="radio"
                    checked={mirrorAxis === "horizontal"}
                    onChange={() =>
                      setMirrorAxis && setMirrorAxis("horizontal")
                    }
                  />{" "}
                  Horizontal (flip top↔bottom)
                </label>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#3a4d9f",
                  marginBottom: 8,
                  padding: 6,
                  background: "#dde5fb",
                  borderRadius: 4,
                }}
              >
                Click on the canvas to place the mirror line — a dashed line
                will show where it is. Fine-tune with the field below, or keep
                clicking to move it.
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <span>
                  Mirror line ({mirrorAxis === "vertical" ? "x" : "y"} =)
                </span>
                <input
                  type="number"
                  value={mirrorLine}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setMirrorLine && setMirrorLine(isNaN(v) ? 0 : v);
                  }}
                  step="0.1"
                  style={{
                    width: 80,
                    padding: "3px 6px",
                    border: "1px solid #8ea6e8",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
              </div>
              <button
                onClick={() => onApplyMirror(mirrorAxis, mirrorLine)}
                title="Add mirrored copies of the selected elements"
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  fontSize: 12,
                  background: "#2a3d8f",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Add mirror to plan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Convert-selection-to-room bar (Stage 4c) — appears when the
          selected walls form a closed axis-aligned rectangle. Clicking
          a type button removes those walls and adds a room element with
          the derived x/y/width/height. */}
      {canConvertSelection && onConvertSelection && (
        <div
          style={{
            padding: 8,
            marginBottom: 8,
            background: "#fff8dc",
            border: "1px solid #f0c040",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6, color: "#664400" }}>
            Convert selection to room:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {[
              "BEDROOM",
              "BATHROOM",
              "KITCHEN",
              "LIVING",
              "DINING",
              "HALL",
              "STORAGE",
              "BALCONY",
            ].map((t) => (
              <button
                key={t}
                onClick={() => onConvertSelection(t)}
                title={`Convert selected walls to ${t}`}
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  background: "#fff",
                  color: "#333",
                  border: "1px solid #bbb",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                {t.charAt(0) + t.slice(1).toLowerCase()}
              </button>
            ))}
            {onConvertToStairs && (
              <button
                onClick={onConvertToStairs}
                title="Convert selected walls to STAIRS (edit steps / direction after)"
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  background: "#eef7f0",
                  color: "#005f52",
                  border: "1px solid #2e8b57",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Stairs
              </button>
            )}
            {onConvertToShaft && (
              <button
                onClick={onConvertToShaft}
                title="Convert selected walls to STAIR SHAFT (U-shape indoor, edit after)"
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  background: "#e3f2fd",
                  color: "#01579b",
                  border: "1px solid #1976d2",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Stair Shaft
              </button>
            )}
          </div>
        </div>
      )}

      {floor.rooms.map((r, i) => {
        const editing = editIndex === i && editKind !== "custom";
        const selected = isSelected("room", i);
        return (
          <Row
            key={`r${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("room", i);
              }
            }}
          >
            <strong>{r.label || r.name}</strong> ({r.type}){" — "}
            {r.width}×{r.height}m @ ({r.x},{r.y})
            {editing && (
              <span style={{ color: "#a06000", fontWeight: 600 }}>
                {" "}
                — editing…
              </span>
            )}
            {!editing && (
              <EditBtn onClick={() => onEditElement(i, "room")}>Edit</EditBtn>
            )}
            {editing && onCancelEdit && (
              <CancelBtn onClick={onCancelEdit}>Cancel</CancelBtn>
            )}
            <DeleteBtn onClick={() => onDeleteElement(i, "room")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}

      {/* Customs — editable in Stage 3a */}
      {(floor.customs || []).map((c, i) => {
        const editing = editIndex === i && editKind === "custom";
        const selected = isSelected("custom", i);
        const nPts = (c.points || []).length;
        return (
          <Row
            key={`c${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("custom", i);
              }
            }}
          >
            <strong>{c.label || c.name}</strong> (CUSTOM)
            {" — "}
            {nPts} pts, {c.closed ? "closed" : "open"}
            {editing && (
              <span style={{ color: "#a06000", fontWeight: 600 }}>
                {" "}
                — editing…
              </span>
            )}
            {!editing && (
              <EditBtn onClick={() => onEditElement(i, "custom")}>Edit</EditBtn>
            )}
            {editing && onCancelEdit && (
              <CancelBtn onClick={onCancelEdit}>Cancel</CancelBtn>
            )}
            <DeleteBtn onClick={() => onDeleteElement(i, "custom")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}

      {/* Shafts — editable in Stage 3c */}
      {(floor.shafts || []).map((s, i) => {
        const editing = editIndex === i && editKind === "shaft";
        const selected = isSelected("shaft", i);
        const nElev = (s.elevators || []).length;
        return (
          <Row
            key={`sh${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("shaft", i);
              }
            }}
          >
            <strong>{s.label || s.name}</strong> (SHAFT)
            {" — "}steps {s.steps}, {s.width_dir?.toUpperCase()}-width
            {nElev > 0 && `, ${nElev} elevator${nElev > 1 ? "s" : ""}`} @ ({s.x}
            ,{s.y})
            {editing && (
              <span style={{ color: "#a06000", fontWeight: 600 }}>
                {" "}
                — editing…
              </span>
            )}
            {!editing && (
              <EditBtn onClick={() => onEditElement(i, "shaft")}>Edit</EditBtn>
            )}
            {editing && onCancelEdit && (
              <CancelBtn onClick={onCancelEdit}>Cancel</CancelBtn>
            )}
            <DeleteBtn onClick={() => onDeleteElement(i, "shaft")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}

      {/* Stairs — editable in Stage 3h */}
      {(floor.stairs || []).map((s, i) => {
        const editing = editIndex === i && editKind === "stairs";
        const selected = isSelected("stairs", i);
        return (
          <Row
            key={`s${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("stairs", i);
              }
            }}
          >
            <strong>{s.label || s.name}</strong> (STAIRS)
            {" — "}steps {s.steps}, {s.width_dir?.toUpperCase()}-width @ ({s.x},
            {s.y})
            {editing && (
              <span style={{ color: "#a06000", fontWeight: 600 }}>
                {" "}
                — editing…
              </span>
            )}
            {!editing && (
              <EditBtn onClick={() => onEditElement(i, "stairs")}>Edit</EditBtn>
            )}
            {editing && onCancelEdit && (
              <CancelBtn onClick={onCancelEdit}>Cancel</CancelBtn>
            )}
            <DeleteBtn onClick={() => onDeleteElement(i, "stairs")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}
      {/* Walls — editable in Stage 3j */}
      {(floor.beams || []).map((w, i) => {
        const editing = editIndex === i && editKind === "beam";
        const selected = isSelected("beam", i);
        const L = Math.hypot(
          (w.x2 || 0) - (w.x1 || 0),
          (w.y2 || 0) - (w.y1 || 0)
        );
        return (
          <Row
            key={`w${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("beam", i);
              }
            }}
          >
            <strong>{w.label || w.name}</strong> (WALL)
            {" — "}
            {L.toFixed(2)}m ({w.x1},{w.y1})→({w.x2},{w.y2})
            {editing && (
              <span style={{ color: "#a06000", fontWeight: 600 }}>
                {" "}
                — editing…
              </span>
            )}
            {!editing && (
              <EditBtn onClick={() => onEditElement(i, "beam")}>Edit</EditBtn>
            )}
            {editing && onCancelEdit && (
              <CancelBtn onClick={onCancelEdit}>Cancel</CancelBtn>
            )}
            <DeleteBtn onClick={() => onDeleteElement(i, "beam")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}

      {/* Columns — Stage 5 */}
      {(floor.columns || []).map((c, i) => {
        const editing = editIndex === i && editKind === "column";
        const selected = isSelected("column", i);
        return (
          <Row
            key={`col${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("column", i);
              }
            }}
          >
            <strong>{c.label || c.name}</strong> (COLUMN)
            {" — "}
            {(c.width || 0).toFixed(2)}×{(c.thickness || 0).toFixed(2)}m [
            {c.direction}
            {c.direction === "angled" ? ` ${c.angle}°` : ""}] @({c.x},{c.y})
            {editing && (
              <span style={{ color: "#a06000", fontWeight: 600 }}>
                {" "}
                — editing…
              </span>
            )}
            {!editing && (
              <EditBtn onClick={() => onEditElement(i, "column")}>Edit</EditBtn>
            )}
            {editing && onCancelEdit && (
              <CancelBtn onClick={onCancelEdit}>Cancel</CancelBtn>
            )}
            <DeleteBtn onClick={() => onDeleteElement(i, "column")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}

      {/* Slabs — Stage 5 */}
      {(floor.slabs || []).map((sl, i) => {
        const editing = false;
        const selected = isSelected("slab", i);
        const npts = (sl.points || []).length;
        return (
          <Row
            key={`slab${i}`}
            $editing={editing}
            $selected={selected}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                onToggleSelection && onToggleSelection("slab", i);
              }
            }}
          >
            <strong>{sl.label || sl.name}</strong> (
            {sl.kind === "solid" ? "SOLID SLAB" : "FLAT SLAB"}){" — "}
            {npts} pts
            <DeleteBtn onClick={() => onDeleteElement(i, "slab")}>
              Delete
            </DeleteBtn>
          </Row>
        );
      })}

      {/* Text labels section (Stage 3z Issue 1) — always visible when
          there are texts. Each row shows the label content, its (x,y),
          and a Delete button. Useful for cleaning up orphan labels that
          got left behind after deleting the walls that surrounded them. */}
      {(floor.texts || []).length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 8,
            borderTop: "1px solid #ddd",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#333",
                flex: 1,
              }}
            >
              Text labels ({(floor.texts || []).length})
            </div>
            {onDeleteOrphanTexts && (
              <button
                onClick={onDeleteOrphanTexts}
                title="Delete labels not inside any room / near any wall"
                style={{
                  padding: "3px 8px",
                  fontSize: 11,
                  background: "#fff",
                  color: "#a06000",
                  border: "1px solid #f0c040",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                🧹 Clean orphans
              </button>
            )}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#666",
              marginBottom: 6,
              fontStyle: "italic",
            }}
          >
            Tip: Ctrl+click a label on canvas or in this list to select
            multiple, then press Delete.
          </div>
          {(floor.texts || []).map((t, i) => {
            const selected = isSelected("text", i);
            return (
              <Row
                key={`txt${i}`}
                $editing={false}
                $selected={selected}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    onToggleSelection && onToggleSelection("text", i);
                  }
                }}
              >
                <strong>{t.content}</strong>
                {" — "}
                <span style={{ color: "#666" }}>
                  ({t.x}, {t.y})
                </span>
                <DeleteBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteText && onDeleteText(i);
                  }}
                >
                  Delete
                </DeleteBtn>
              </Row>
            );
          })}
        </div>
      )}
    </Wrap>
  );
}
